import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { stepperSubtitleKeyMap } from '../shared/evaluationTypeLabelMap';
import { FormNavigator } from '../shared/FormNavigator';
import { RouteName } from '../types';

import type { Step } from '@/components/Stepper/StepItem';

import { useGetCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import {
    lastSavedAtAtom,
    questionnaireCreationOnNextAtom,
    scheduleCreationOnNextAtom,
    selectRevieweeOnNextAtom,
    selectTemplateOnNextAtom,
} from '@/atoms/evaluation360';
import { CycleInstanceSteps } from '@/constants/cycle-evaluation/CycleInstanceSteps';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { QuestionnaireCreationPage } from '@/pages/Evaluation360/QuestionnaireCreation/QuestionnaireCreationPage';
import { ScheduleCreationPage } from '@/pages/Evaluation360/ScheduleCreation/ScheduleCreationPage';
import { SelectRevieweePage } from '@/pages/Evaluation360/SelectReviewee/SelectRevieweePage';
import { SelectTemplatePage } from '@/pages/Evaluation360/SelectTemplate/SelectTemplatePage';
import { SuccessMessageAction } from '@/pages/shared/SuccessMessage/SuccessMessagePage';
import {
    getInitialMaxStep,
    getStepFromPath,
    getStepPath,
    isTemplateStepCompleted,
} from '@/utils/cycleInstance';

const ROUTES = [
    {
        path: `${RouteName.SelfAssessmentForm}/${getStepPath(CycleInstanceSteps.SELECT_REVIEWEE)}/:cycleInstanceId`,
        component: SelectRevieweePage,
    },
    {
        path: `${RouteName.SelfAssessmentForm}/${getStepPath(CycleInstanceSteps.SELECT_TEMPLATE)}/:cycleInstanceId`,
        component: SelectTemplatePage,
    },
    {
        path: `${RouteName.SelfAssessmentForm}/${getStepPath(CycleInstanceSteps.QUESTIONNAIRE_CREATION)}/:cycleInstanceId`,
        component: QuestionnaireCreationPage,
    },
    {
        path: `${RouteName.SelfAssessmentForm}/${getStepPath(CycleInstanceSteps.SCHEDULE_CREATION)}/:cycleInstanceId`,
        component: ScheduleCreationPage,
    },
];

export const SelfAssessmentFormNavigator = () => {
    const navigate = useNavigate();
    const { t } = useAmelioTranslations();
    const { cycleInstanceId, step } = useParams<{ cycleInstanceId: string; step: string }>();

    const { data: cycleInstance } = useGetCycleInstance(cycleInstanceId, {
        includeInactiveQuestions: true,
    });

    const selectRevieweeOnNext = useAtomValue(selectRevieweeOnNextAtom);
    const selectTemplateOnNext = useAtomValue(selectTemplateOnNextAtom);
    const questionnaireCreationOnNext = useAtomValue(questionnaireCreationOnNextAtom);
    const scheduleCreationOnNext = useAtomValue(scheduleCreationOnNextAtom);
    const lastSavedAt = useAtomValue(lastSavedAtAtom);

    // Self-assessment specific steps - excludes ASSIGN_REVIEWER
    const allSteps: Step[] = [
        {
            id: CycleInstanceSteps.SELECT_REVIEWEE,
            label: t('selfAssessmentStepper.selectReviewee.stepTitle'),
        },
        {
            id: CycleInstanceSteps.SELECT_TEMPLATE,
            label: t('selfAssessmentStepper.templateSelection.stepTitle'),
        },
        {
            id: CycleInstanceSteps.QUESTIONNAIRE_CREATION,
            label: t('selfAssessmentStepper.questionnaireCreation.stepTitle'),
        },
        {
            id: CycleInstanceSteps.SCHEDULE_CREATION,
            label: t('selfAssessmentStepper.scheduleCreation.stepTitle'),
        },
    ];

    // Keep the step while the user is on it (via "Change Template"); filtering it out then breaks FormNavigator's step lookup.
    const isOnSelectTemplateStep = getStepFromPath(step) === CycleInstanceSteps.SELECT_TEMPLATE;
    const steps =
        isTemplateStepCompleted(cycleInstance) && !isOnSelectTemplateStep
            ? allSteps.filter((s) => s.id !== CycleInstanceSteps.SELECT_TEMPLATE)
            : allSteps;

    const onNextCallbacks = useMemo(
        () => ({
            [CycleInstanceSteps.SELECT_REVIEWEE]: selectRevieweeOnNext,
            [CycleInstanceSteps.SELECT_TEMPLATE]: selectTemplateOnNext,
            [CycleInstanceSteps.QUESTIONNAIRE_CREATION]: questionnaireCreationOnNext,
            [CycleInstanceSteps.SCHEDULE_CREATION]: scheduleCreationOnNext,
        }),
        [
            selectRevieweeOnNext,
            selectTemplateOnNext,
            questionnaireCreationOnNext,
            scheduleCreationOnNext,
        ],
    );

    const onSuccess = () => {
        navigate({
            path: RouteName.SuccessMessage,
            state: {
                title: t('selfAssessmentSuccessMessage.title'),
                buttonLabel: t('selfAssessmentSuccessMessage.goToDashboard'),
                actionType: SuccessMessageAction.NAVIGATE_TO_EVAL_DASHBOARD,
            },
        });
    };

    const initialMaxStep = getInitialMaxStep({ cycleInstance, steps });

    return (
        <FormNavigator
            steps={steps}
            routePrefix={RouteName.SelfAssessmentForm}
            urlParamName={'cycleInstanceId'}
            getStepFromPath={getStepFromPath}
            getStepPath={getStepPath}
            backRoute={RouteName.Evaluation360}
            onSuccess={onSuccess}
            onNextCallbacks={onNextCallbacks}
            lastSavedAt={lastSavedAt}
            stepperTitle={t('selfAssessmentStepper.title')}
            stepperSubtitle={t(stepperSubtitleKeyMap.self)}
            stepperDataTestId={'self-assessment-stepper'}
            contentClassName={'stepper-center'}
            routes={ROUTES}
            initialMaxStep={initialMaxStep}
        />
    );
};
